/**
 * One history for every object.
 *
 * Any object that tracks history records its changes here, through
 * `recordObjectChange`, and any surface reads them with `useObjectHistory`.
 * A Question's version list is the richer form of the same idea (it keeps the
 * content of each version); this is the audit trail every other object gets
 * for free: who changed what, when, from what to what.
 *
 * Kept in this tab's storage, newest first, capped per object.
 */

import * as React from "react"

import type { HistoryEntry, ObjectType } from "./types"

const STORE_KEY = "exxat.exam.object-history:v1"
const MAX_PER_OBJECT = 100

let entries: HistoryEntry[] | undefined
const listeners = new Set<() => void>()
let snapshotVersion = 0

function load(): HistoryEntry[] {
  if (entries) return entries
  try {
    const raw = typeof window !== "undefined" ? window.localStorage.getItem(STORE_KEY) : null
    entries = raw ? (JSON.parse(raw) as HistoryEntry[]) : []
  } catch {
    entries = []
  }
  return entries
}

function persist() {
  try {
    window.localStorage.setItem(STORE_KEY, JSON.stringify(entries ?? []))
  } catch {
    // Storage can be blocked; the in-memory history still serves this session.
  }
}

export function recordObjectChange(input: {
  type: ObjectType
  objectId: string
  by: string
  summary: string
  changes?: HistoryEntry["changes"]
  at?: string
}): HistoryEntry {
  const entry: HistoryEntry = {
    id: `${input.type}:${input.objectId}:${Date.now().toString(36)}:${Math.random().toString(36).slice(2, 6)}`,
    type: input.type,
    objectId: input.objectId,
    at: input.at ?? new Date().toISOString(),
    by: input.by,
    summary: input.summary,
    changes: input.changes,
  }
  const all = load()
  const own = all.filter((e) => e.type === input.type && e.objectId === input.objectId)
  const rest = all.filter((e) => !(e.type === input.type && e.objectId === input.objectId))
  entries = [entry, ...own].slice(0, MAX_PER_OBJECT).concat(rest)
  snapshotVersion += 1
  persist()
  listeners.forEach((listener) => listener())
  return entry
}

/** Newest first. */
export function readObjectHistory(type: ObjectType, objectId: string): HistoryEntry[] {
  return load().filter((e) => e.type === type && e.objectId === objectId)
}

/** Test and reset support. */
export function clearObjectHistory() {
  entries = []
  snapshotVersion += 1
  persist()
  listeners.forEach((listener) => listener())
}

const subscribe = (listener: () => void) => {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

export function useObjectHistory(type: ObjectType, objectId: string): HistoryEntry[] {
  const version = React.useSyncExternalStore(
    subscribe,
    () => snapshotVersion,
    () => 0,
  )
  return React.useMemo(
    () => readObjectHistory(type, objectId),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [type, objectId, version],
  )
}
